import { useEffect, useRef, useState } from 'react';
import { openPdf, type PdfPages } from '@core/files';

/**
 * PDF постранично.
 *
 * Страница рисуется только тогда, когда доезжает до экрана, и отпускается, когда
 * уезжает далеко: том дела на триста листов нельзя держать в памяти целиком
 * ([NFR.md](docs/NFR.md)). До отрисовки место занимает пустая страница нужных
 * пропорций — иначе список прыгал бы под пальцем.
 */

/** Сколько страниц вокруг видимой держим нарисованными. */
const KEEP_AROUND = 1;

export function PdfView({ content, onFailed }: { content: Blob; onFailed: () => void }) {
  const [pages, setPages] = useState<PdfPages | null>(null);
  const [count, setCount] = useState(0);
  const [ratios, setRatios] = useState<number[]>([]);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let closed = false;
    let opened: PdfPages | null = null;

    void (async () => {
      opened = await openPdf(content);
      if (closed) return void opened?.close();

      if (!opened) {
        onFailed();
        return;
      }

      setPages(opened);
      setCount(opened.pageCount);

      // Пропорции всех страниц читаются сразу: это заголовки, а не содержимое,
      // и без них не занять правильное место под каждую страницу.
      const sizes: number[] = [];
      for (let page = 1; page <= opened.pageCount; page += 1) sizes.push(await opened.ratio(page));
      if (!closed) setRatios(sizes);
    })();

    return () => {
      closed = true;
      void opened?.close();
    };
  }, [content, onFailed]);

  if (!pages) return <div className="viewer__loading">Открываем документ…</div>;

  return (
    <div className="viewer__pages" ref={box}>
      {Array.from({ length: count }, (_, index) => (
        <PdfPage key={index} pages={pages} number={index + 1} ratio={ratios[index] ?? 1.414} />
      ))}
    </div>
  );
}

function PdfPage({ pages, number, ratio }: { pages: PdfPages; number: number; ratio: number }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const holder = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const element = holder.current;
    if (!element) return;

    const watcher = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        setDrawn(visible);
      },
      // Соседние страницы рисуются заранее: иначе при прокрутке видно пустое место.
      { rootMargin: `${KEEP_AROUND * 100}% 0px` },
    );

    watcher.observe(element);
    return () => watcher.disconnect();
  }, []);

  useEffect(() => {
    if (!drawn || !canvas.current) return;

    let dropped = false;
    const width = holder.current?.clientWidth ?? 0;
    if (width === 0) return;

    void pages.render(number, canvas.current, width).catch(() => null);

    return () => {
      dropped = true;
      void dropped;
    };
  }, [drawn, pages, number]);

  return (
    <div
      ref={holder}
      className="viewer__page"
      style={{ aspectRatio: `1 / ${ratio}` }}
      data-page={number}
    >
      {drawn ? <canvas ref={canvas} className="viewer__canvas" /> : null}
    </div>
  );
}
